// NavLinks.tsx - Simple Gradient Badge Implementation for Chat

import React from 'react';
import { NavLink } from 'react-router-dom';
import { Brain, BookOpen, Aperture, BarChart, MessageSquare } from 'lucide-react';
import { useWorkspaceRole } from '@/utils/hooks/useWorkspaceRole';
import { useTranslation } from 'react-i18next';

interface NavLinksProps {
  isMobile?: boolean;
  onItemClick?: () => void;
}

// Simple Gradient Badge Component
const GradientBetaBadge: React.FC<{ isActive: boolean }> = ({ isActive }) => (
  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold transition-all ml-1 ${
    isActive 
      ? "bg-white/20 text-white border border-white/30" 
      : "bg-gradient-to-r from-orange-400 to-orange-600 text-white shadow-sm"
  }`}>
    β
  </span>
);

export const NavLinks: React.FC<NavLinksProps> = ({ isMobile = false, onItemClick }) => {
  const workspaceRole = useWorkspaceRole();

  const baseClasses = "flex items-center gap-2 px-3 py-2 rounded-full transition-colors";
  const activeClasses = "bg-primary text-white dark:bg-primary-dark dark:text-primary-foreground";
  const inactiveClasses = "bg-ghost text-muted-foreground hover:text-foreground";

  const isAdminOrCreator = workspaceRole.isWorkspaceAdmin || workspaceRole.isWorkspaceCreator;
  const isAdminOrCreatorOrUser = workspaceRole.isWorkspaceAdmin || workspaceRole.isWorkspaceCreator || workspaceRole.isWorkspaceUser;
  const { t } = useTranslation();

  // Feature flags
  const isChatEnabled = true;

  const handleClick = () => {
    if (isMobile && onItemClick) {
      onItemClick();
    }
  };

  return (
    <>
      {isAdminOrCreator && (
        <>
          <NavLink to="/providers" className={({ isActive }) => `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`} onClick={handleClick}>
            <Brain className="h-5 w-5" />
            <span>{t('menu.providers')}</span>
          </NavLink>
          <NavLink to="/knowledges" className={({ isActive }) => `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`} onClick={handleClick}>
            <BookOpen className="h-5 w-5" />
            <span>{t('menu.knowledges')}</span>
          </NavLink>
          <NavLink to="/studio" className={({ isActive }) => `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`} onClick={handleClick}>
            <Aperture className="h-5 w-5" />
            <span>{t('menu.studio')}</span>
          </NavLink>
        </>
      )}

      {isChatEnabled && (
        <NavLink to="/chat" className={({ isActive }) => `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`} onClick={handleClick}>
          {({ isActive }) => (
            <>
              <MessageSquare className="h-5 w-5" />
              <span>{t('menu.internalChat')}</span>
              <GradientBetaBadge isActive={isActive} />
            </>
          )}
        </NavLink>
      )}

      {isAdminOrCreatorOrUser && (
        <>
          <NavLink to="/analytics" className={({ isActive }) => `${baseClasses} ${isActive ? activeClasses : inactiveClasses}`} onClick={handleClick}>
            <BarChart className="h-5 w-5" />
            <span>{t('menu.analytics')}</span>
          </NavLink>
        </>
      )}
    </>
  );
};